import { useEffect, useState } from "react";
import { CreateBase, Form, useGetIdentity, useGetOne, useNotify } from "ra-core";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { FormToolbar } from "../layout/FormToolbar";
import { LoadInputs } from "./LoadInputs";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Fields that the legacy duplicate logic resets to blank / default values.
// We omit them from the default values so the form starts clean.
const CLEARED_ON_DUPLICATE = [
  "id",
  "legacy_suitecrm_id",
  "status",
  "pickup_date",
  "booking_number",
  "seal_number",
  "container_number",
  "booking_requested",
  "hold_reason",
  "invoiced_by_user_id",
  "supplier_requirements",
  "customer_requirements",
  "vendor_invoice_total",
  "customer_invoice_total",
  "vendor_date_paid",
  "customer_date_paid",
  "ready_to_invoice",
  // calculated outputs — recalculated after save
  "total_freight",
  "total_freight_markup",
  "total_customs",
  "vendor_total",
  "vendor_total_usd",
  "customer_total",
  "customer_total_usd",
  "profit_usd",
  "profit_cad",
  "margin",
  "created_at",
  "updated_at",
  "deleted",
];

export const LoadCreate = () => {
  const { identity } = useGetIdentity();
  const [searchParams] = useSearchParams();
  const notify = useNotify();
  const navigate = useNavigate();

  const duplicateOfId = searchParams.get("duplicateOf");
  const isDuplicate = !!duplicateOfId;

  // Fetch the source load when duplicating.
  const { data: sourceLoad } = useGetOne(
    "loads",
    { id: Number(duplicateOfId) },
    { enabled: isDuplicate },
  );

  const [defaultValues, setDefaultValues] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (!isDuplicate) {
      setDefaultValues({ sales_owner_id: identity?.id });
      return;
    }
    if (!sourceLoad) return;

    // Strip cleared fields, then apply legacy overrides.
    const base: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(sourceLoad)) {
      if (!CLEARED_ON_DUPLICATE.includes(key)) {
        base[key] = value;
      }
    }

    base.load_number = undefined; // trigger assigns a fresh one on insert
    base.status = "pending_so_approval";
    base.freight_terms = "CPT";
    base.customer_terms = "";
    base.vendor_terms = "";
    base.vendor_payment_method = "";
    base.internal_notes =
      `Load Duplicate of: ${sourceLoad.load_number ?? sourceLoad.id}` +
      (sourceLoad.internal_notes ? `\n\n${sourceLoad.internal_notes}` : "");

    setDefaultValues(base);
  }, [sourceLoad, identity?.id, isDuplicate]);

  // After the new load is saved, copy child rows via the edge function.
  const handleSuccess = async (newLoad: { id: number }) => {
    if (isDuplicate && duplicateOfId) {
      try {
        const { error } = await getSupabaseClient().functions.invoke(
          "duplicate_load",
          {
            body: {
              source_load_id: Number(duplicateOfId),
              new_load_id: newLoad.id,
            },
          },
        );
        if (error) {
          console.error("duplicate_load error:", error);
          notify("Load created, but some child rows could not be copied.", { type: "warning" });
        }
      } catch (err) {
        console.error("duplicate_load error:", err);
        notify("Load created, but some child rows could not be copied.", { type: "warning" });
      }
    }
    navigate(`/loads/${newLoad.id}/show`);
  };

  // Wait until default values are ready before rendering the form,
  // so inputs don't render empty and then re-render with values.
  if (isDuplicate && defaultValues === null) return null;

  return (
    <CreateBase
      redirect={false}
      mutationOptions={{ onSuccess: handleSuccess }}
    >
      <div className="mt-2 flex flex-col gap-4 max-w-5xl">
        <h1 className="text-2xl font-bold">
          {isDuplicate
            ? `Duplicate of Load #${sourceLoad?.load_number ?? duplicateOfId}`
            : "New Load"}
        </h1>
        <Form defaultValues={defaultValues ?? { sales_owner_id: identity?.id }}>
          <Card>
            <CardContent className="pt-6">
              <LoadInputs />
              <FormToolbar />
            </CardContent>
          </Card>
        </Form>
      </div>
    </CreateBase>
  );
};
